import { Controller } from "@hotwired/stimulus"
import { MeiliSearch } from "meilisearch"

// Add/remove plant links (relations) with MeiliSearch autocomplete
// Targets: container
export default class extends Controller {
  static targets = ["container"]

  connect() {
    const env = document.querySelector('meta[name=env]')?.content || 'development'
    const client = new MeiliSearch({ host: location.protocol + '//' + location.host })
    this.plantsIndex = client.index('Plant_' + env)
    this.plantLinksIndex = client.index('PlantLinks_' + env)
    this.counter = this.containerTarget.querySelectorAll("[data-link-row]").length

    this.handleOutsideClick = this.handleOutsideClick.bind(this)
    document.addEventListener("click", this.handleOutsideClick)
  }

  disconnect() {
    document.removeEventListener("click", this.handleOutsideClick)
  }

  handleOutsideClick(event) {
    if (!event.target.closest("[data-link-type-dropdown]") && !event.target.closest("[data-link-type-input]")) {
      this.element.querySelectorAll("[data-link-type-dropdown]").forEach(el => el.innerHTML = "")
    }
    if (!event.target.closest("[data-to-plant-dropdown]") && !event.target.closest("[data-to-plant-input]")) {
      this.element.querySelectorAll("[data-to-plant-dropdown]").forEach(el => el.innerHTML = "")
    }
  }

  addLink(event) {
    event.preventDefault()
    const row = this.buildNewLinkRow(this.counter)
    this.counter++
    this.containerTarget.insertAdjacentHTML("beforeend", row)
  }

  removeLink(event) {
    event.preventDefault()
    const row = event.target.closest("[data-link-row]")
    if (!row) return

    const linkId = row.dataset.linkId
    const persisted = row.dataset.persisted === "true"

    if (persisted && linkId) {
      // Replace row with destroy hidden fields
      row.innerHTML = `<input type="hidden" name="plant[from_plant_links_attributes][][id]" value="${linkId}" />
        <input type="hidden" name="plant[from_plant_links_attributes][][_destroy]" value="true" />`
    } else {
      row.remove()
    }
  }

  removeToPlant(event) {
    event.preventDefault()
    const row = event.target.closest("[data-link-row]")
    if (!row) return

    const preview = row.querySelector("[data-plant-preview]")
    const searchArea = row.querySelector("[data-plant-search]")
    const hiddenInput = row.querySelector("[data-to-plant-id-input]")

    if (preview) preview.remove()
    if (hiddenInput) hiddenInput.remove()
    if (searchArea) searchArea.style.display = ""

    // Update link_type input name to not submit without plant
    const linkTypeInput = row.querySelector("[data-link-type-input]")
    if (linkTypeInput) linkTypeInput.removeAttribute("name")
  }

  searchLinkType(event) {
    const input = event.target
    const row = input.closest("[data-link-row]")
    const dropdown = row.querySelector("[data-link-type-dropdown]")

    this.plantLinksIndex.search(input.value, {
      hitsPerPage: 10,
      distinct: 1
    }).then(({ hits }) => {
      if (hits.length > 0) {
        dropdown.innerHTML = hits.map(hit =>
          `<a data-action="click->plant-links#selectLinkType" data-link-type="${this.escapeHtml(hit.link_type)}"><div>${this.escapeHtml(hit.link_type)}</div></a>`
        ).join("")
      } else {
        dropdown.innerHTML = ""
      }
    })
  }

  selectLinkType(event) {
    event.preventDefault()
    const row = event.target.closest("[data-link-row]")
    const input = row.querySelector("[data-link-type-input]")
    const dropdown = row.querySelector("[data-link-type-dropdown]")
    const value = event.target.closest("[data-link-type]")?.dataset.linkType || event.target.textContent
    input.value = value
    this.updateLinkTypeInputName(row)
    dropdown.innerHTML = ""
  }

  searchToPlant(event) {
    const input = event.target
    const row = input.closest("[data-link-row]")
    const dropdown = row.querySelector("[data-to-plant-dropdown]")

    this.plantsIndex.search(input.value, {
      hitsPerPage: 10
    }).then(({ hits }) => {
      if (hits.length > 0) {
        dropdown.innerHTML = hits.map(hit =>
          `<div class="input-search-result">
            <a class="flex items-center" data-action="click->plant-links#selectToPlant" data-plant-id="${hit.id}" data-plant-name="${this.escapeHtml(hit.name)}" data-plant-thumb="${this.escapeHtml(hit.thumb || '')}">
              <img class="data-img-xs" src="${this.escapeHtml(hit.thumb || '')}" />
              <label class="input-search-result-name"> ${this.escapeHtml(hit.name)} <span>${this.escapeHtml(hit.scientific_name || '')}</span></label>
            </a>
          </div>`
        ).join("")
      } else {
        dropdown.innerHTML = ""
      }
    })
  }

  selectToPlant(event) {
    event.preventDefault()
    const anchor = event.target.closest("[data-plant-id]")
    if (!anchor) return

    const row = anchor.closest("[data-link-row]")
    const plantId = anchor.dataset.plantId
    const plantName = anchor.dataset.plantName
    const plantThumb = anchor.dataset.plantThumb
    const searchArea = row.querySelector("[data-plant-search]")
    const dropdown = row.querySelector("[data-to-plant-dropdown]")

    dropdown.innerHTML = ""
    searchArea.style.display = "none"

    // Insert plant preview and hidden input
    const preview = `<div data-plant-preview class="img-preview-container row row-between items-center p-2">
      <input type="hidden" name="plant[from_plant_links_attributes][][to_plant_id]" value="${plantId}" data-to-plant-id-input />
      <div>
        <img class="data-img-xs" src="${this.escapeHtml(plantThumb)}" />
        <span class="pl-4">${this.escapeHtml(plantName)}</span>
      </div>
      <a class="btn-icon-clear-danger" data-action="click->plant-links#removeToPlant" href="">
        <svg class="icon"><use href="/icons/__remixicon.sprite.svg#close-line"/></svg>
      </a>
    </div>`

    searchArea.insertAdjacentHTML("afterend", preview)
    this.updateLinkTypeInputName(row)
  }

  updateLinkTypeInputName(row) {
    const linkTypeInput = row.querySelector("[data-link-type-input]")
    const hasToPlant = row.querySelector("[data-to-plant-id-input]")
    if (linkTypeInput && linkTypeInput.value && hasToPlant) {
      linkTypeInput.setAttribute("name", "plant[from_plant_links_attributes][][link_type]")
    }
  }

  buildNewLinkRow(index) {
    return `<div class="border-box mb-4" data-link-row="${index}" data-persisted="false">
      <div class="row row-between mb-4">
        <div class="col-90 dropdown-wrapper">
          <input data-link-type-input
            required
            type="text"
            class="input-text"
            placeholder="Describe the connection"
            autocomplete="off"
            data-action="focus->plant-links#searchLinkType input->plant-links#searchLinkType"
          />
          <div data-link-type-dropdown class="dropdown text-left"></div>
        </div>
        <div class="col-10 text-right">
          <button class="btn-icon-clear-danger" data-action="click->plant-links#removeLink" type="button">
            <svg class="icon icon-gray"><use href="/icons/__remixicon.sprite.svg#delete-bin-line"/></svg>
          </button>
        </div>
      </div>
      <div data-plant-search class="dropdown-wrapper">
        <input required
          data-to-plant-input
          class="input-text"
          placeholder="Search for plants here"
          type="text"
          data-action="focus->plant-links#searchToPlant input->plant-links#searchToPlant"
        />
        <div data-to-plant-dropdown class="dropdown text-left"></div>
      </div>
    </div>`
  }

  escapeHtml(str) {
    if (!str) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
